import React, { useContext, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, CircularProgress } from "@mui/material";
import PropertyContext from "../contexts/PropertyContext";

const GenerateDRLFromTemplate = () => {
  const [loading, setLoading] = useState(false);
  const { rooms, items } = useContext(PropertyContext);

  // Helper functions for converting requirements to minutes
  const convertHousekeeping = (req) => {
    const housekeepingMapping = {
      Refresh: 15,
      Easy: 45,
      Medium: 90,
      Heavy: 120,
      "Very Heavy": 180,
      Completed: 0,
    };
    return housekeepingMapping[req] ?? 0;
  };

  const convertMaintenance = (req) => {
    const maintenanceMapping = {
      Easy: 30,
      Medium: 60,
      Heavy: 90,
      "Very Heavy": 120,
      Completed: 0,
    };
    return maintenanceMapping[req] ?? 0;
  };

  const generateDRLFromTemplate = async () => {
    setLoading(true);
    try {
      console.log("Loading template file...");

      // Load the template workbook
      const workbook = new ExcelJS.Workbook();
      const response = await fetch("/SuzieDownRoomsBase.xlsx");
      const arrayBuffer = await response.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];

      // Parse row 1 to get field mappings
      const fieldMappings = {};
      const row1 = worksheet.getRow(1);

      // Debug: Log all cells in row 1 to see what's there
      console.log("=== Debugging Row 1 Cells ===");
      for (let col = 1; col <= 50; col++) {
        const cell = row1.getCell(col);
        if (cell.value) {
          console.log(`Column ${col} (${String.fromCharCode(64 + col)}):`, cell.value);
        }
      }
      console.log("=== End Row 1 Debug ===");

      row1.eachCell({ includeEmpty: false }, (cell, colNum) => {
        console.log(`Processing cell at column ${colNum}:`, cell.value, typeof cell.value);
        if (cell.value && typeof cell.value === "string") {
          try {
            const fields = JSON.parse(cell.value);
            if (Array.isArray(fields)) {
              fieldMappings[colNum] = fields;
              console.log(`Mapped column ${colNum} to fields:`, fields);
            }
          } catch (e) {
            console.log(`Column ${colNum} is not valid JSON:`, e.message);
          }
        }
      });

      console.log("Field mappings:", fieldMappings);

      // Debug: Check specifically for column O (column 15)
      console.log("Column O (15) mapping:", fieldMappings[15]);
      console.log("Cell O1 direct access:", row1.getCell(15).value);

      // Create a set of all item IDs that are mapped in the template
      const mappedItemIDs = new Set();
      Object.values(fieldMappings).forEach((fields) => {
        fields.forEach((fieldId) => mappedItemIDs.add(fieldId));
      });

      // Debug: Check if itemCountLamp is in the mapped items
      console.log("Is itemCountLamp mapped?", mappedItemIDs.has("itemCountLamp"));

      // Get all rooms with status 'Down'
      const downRooms = rooms.filter(
        (room) => room.status === "Down" || room.status === "Facilities"
      );
      console.log(`Found ${downRooms.length} down rooms`);

      // Debug: Check how many rooms have itemCountLamp > 0
      const roomsWithLamps = downRooms.filter(room => {
        const lampCount = parseFloat(room.itemCountLamp || 0);
        return lampCount > 0;
      });
      console.log(`Rooms with lamps: ${roomsWithLamps.length}`, roomsWithLamps.map(r => ({
        room: r.displayName,
        lampCount: r.itemCountLamp
      })));

      // Sort rooms by room number/name for organization
      downRooms.sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

      // Build a map of existing rooms in the template (room number -> row number)
      const existingRooms = new Map();
      const emptyRows = []; // Track empty rows that can be filled

      // Scan to find existing rooms and empty rows
      // We scan a reasonable range of rows (4 to 100)
      for (let i = 4; i <= 100; i++) {
        const row = worksheet.getRow(i);
        const roomNumber = row.getCell(2).value; // Column B (ROOM #)

        // Check if it's a data row with a room number
        if (roomNumber && typeof roomNumber === 'string' && roomNumber.trim() !== '') {
          existingRooms.set(roomNumber, i);
          console.log(`Found existing room ${roomNumber} at row ${i}`);
        } else {
          // This is an empty row (no room number) - can be used for new rooms
          emptyRows.push(i);
        }
      }

      console.log(`Found ${existingRooms.size} existing rooms in template, ${emptyRows.length} empty rows available`);

      // Track which empty row to use next
      let emptyRowIndex = 0;

      // Process each down room
      for (const room of downRooms) {
        const displayName = room.displayName || "";

        // Check if this room already exists in the template
        let targetRow;
        let isExistingRoom = false;
        let actualRowNumber;

        if (existingRooms.has(displayName)) {
          // Update existing row
          actualRowNumber = existingRooms.get(displayName);
          targetRow = worksheet.getRow(actualRowNumber);
          isExistingRoom = true;
          console.log(`Updating existing room ${displayName} at row ${actualRowNumber}`);
        } else {
          // Use next available empty row
          if (emptyRowIndex >= emptyRows.length) {
            console.error(`No more empty rows available! Need to add more rows to template.`);
            alert(`Template ran out of empty rows. Please add more rows to the template file.`);
            break;
          }

          actualRowNumber = emptyRows[emptyRowIndex];
          targetRow = worksheet.getRow(actualRowNumber);
          emptyRowIndex++; // Move to next empty row for next room
          console.log(`Filling empty row ${actualRowNumber} with new room ${displayName}`);
        }

        const newRow = targetRow;

        // Extract building and room number from displayName (e.g., "A-103" -> building: "A", room: "A-103")
        const building = displayName.split("-")[0] || "";

        // Column 1: BLDG
        newRow.getCell(1).value = building;

        // Column 2: ROOM #
        newRow.getCell(2).value = displayName;

        // Calculate housekeeping and maintenance times (in minutes)
        let housekeepingTotal = convertHousekeeping(
          room.selectedHousekeepingRequirements
        );
        let maintenanceTotal = convertMaintenance(
          room.selectedMaintenanceRequirements
        );

        // Add item difficulty to housekeeping and maintenance totals
        items.forEach((item) => {
          const itemCount = parseFloat(room[item.itemID] || 0);
          const itemDifficulty = parseFloat(item.itemDifficulty || 0);

          if (itemCount > 0) {
            if (item.itemResponsibility === "Maintenance") {
              maintenanceTotal += itemCount * itemDifficulty;
            } else if (item.itemResponsibility === "Housekeeping") {
              housekeepingTotal += itemCount * itemDifficulty;
            }
          }
        });

        // Convert minutes to decimal hours and set in columns
        // Column C (3) = Housekeeping hours
        if (housekeepingTotal > 0) {
          newRow.getCell(3).value = parseFloat(
            (housekeepingTotal / 60).toFixed(2)
          );
        }

        // Column D (4) = Maintenance hours
        if (maintenanceTotal > 0) {
          newRow.getCell(4).value = parseFloat(
            (maintenanceTotal / 60).toFixed(2)
          );
        }

        // Handle paint items specially
        const orangePaint = parseFloat(room["itemCountOrangePaint"] || 0);
        const whitePaint = parseFloat(room["itemCountWhitePaint"] || 0);
        const totalPaint = orangePaint + whitePaint;

        // Column H (8) = Touch Up Paint, Column I (9) = Full Paint
        if (totalPaint > 0) {
          if (totalPaint < 3) {
            // Touch Up Paint
            newRow.getCell(8).value = totalPaint;
          } else {
            // Full Paint
            newRow.getCell(9).value = 1;
          }
        }

        // Populate columns based on field mappings from row 1
        Object.entries(fieldMappings).forEach(([colNum, fields]) => {
          const col = parseInt(colNum);
          let value = 0;

          // Sum up values if multiple fields are mapped to one column
          fields.forEach((fieldId) => {
            const fieldValue = room[fieldId];
            if (fieldValue !== undefined && fieldValue !== null) {
              // Convert to number if it's a string number
              const numValue = parseFloat(fieldValue);
              value += isNaN(numValue) ? 0 : numValue;
            }
          });

          // Only set non-zero values (cleaner spreadsheet)
          if (value > 0) {
            newRow.getCell(col).value = value;
          }
        });

        // Column 39: VALUE (formula - copy from template row 4)
        const templateRow4 = worksheet.getRow(4);
        const valueFormula = templateRow4.getCell(39).formula;
        if (valueFormula) {
          // Update row reference in formula (replace "4" with actual row number)
          // This regex matches: A4, $A4, A$4, $A$4, etc.
          const updatedFormula = valueFormula.replace(
            /(\$?[A-Z]+\$?)4/g,
            `$1${actualRowNumber}`
          );
          newRow.getCell(39).value = { formula: updatedFormula };
        }

        // Column 40 (AN): Last Date Down
        if (room.lastDateDown) {
          newRow.getCell(40).value = new Date(room.lastDateDown);
          newRow.getCell(40).numFmt = "mm/dd/yyyy";
        }

        // Column 41 (AO): Date Room was determined to go to REHAB
        // For facilities rooms, use the down date instead
        if (room.status === "Facilities" && room.lastDateDown) {
          newRow.getCell(41).value = new Date(room.lastDateDown);
          newRow.getCell(41).numFmt = "mm/dd/yyyy";
        } else if (room.dateToRehab) {
          newRow.getCell(41).value = new Date(room.dateToRehab);
          newRow.getCell(41).numFmt = "mm/dd/yyyy";
        }

        // Column 42: Notes - including additional items not in template
        let notesText = room.unitNotes || "";

        // Find items that are needed but not in the template
        const additionalItems = [];
        let additionalItemsCost = 0;

        // Paint items to exclude from additional items (handled separately)
        const paintItemIDs = new Set([
          "itemCountOrangePaint",
          "itemCountWhitePaint",
        ]);

        items.forEach((item) => {
          const itemCount = parseFloat(room[item.itemID] || 0);

          // If item count > 0 and it's NOT in the template mappings and NOT a paint item
          if (
            itemCount > 0 &&
            !mappedItemIDs.has(item.itemID) &&
            !paintItemIDs.has(item.itemID)
          ) {
            const itemCost = parseFloat(item.itemCost || 0);
            const totalCost = itemCount * itemCost;

            additionalItems.push({
              name: item.itemName,
              count: itemCount,
              unitCost: itemCost,
              totalCost: totalCost,
            });

            additionalItemsCost += totalCost;
          }
        });

        // Append additional items section if any exist
        if (additionalItems.length > 0) {
          if (notesText) notesText += "\n\n";
          notesText += "Additionally Missing Items:\n";

          additionalItems.forEach((item) => {
            notesText += `- ${item.name}: ${
              item.count
            } x $${item.unitCost.toFixed(2)} = $${item.totalCost.toFixed(2)}\n`;
          });

          notesText += `\nAdditional Items Total Cost: $${additionalItemsCost.toFixed(
            2
          )}`;
        }

        if (notesText) {
          newRow.getCell(42).value = notesText;
        }

        // Column 43: Days Down (formula - copy from template)
        const daysDownFormula = templateRow4.getCell(43).formula;
        if (daysDownFormula) {
          // Update row reference in formula (replace specific row references)
          const updatedFormula = daysDownFormula.replace(
            /([A-Z]+)4/g,
            `$1${actualRowNumber}`
          );
          newRow.getCell(43).value = { formula: updatedFormula };
        }

        newRow.commit();
      }

      // Set current date in cell AP2 (column 42, row 2)
      const row2 = worksheet.getRow(2);
      row2.getCell(42).value = new Date();
      row2.getCell(42).numFmt = "mm/dd/yyyy";
      row2.commit();

      // Generate the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const today = new Date();
      const dateStr = today.toISOString().split("T")[0].replace(/-/g, "_");
      saveAs(blob, `DRL_Template_${dateStr}.xlsx`);

      console.log("DRL generated successfully!");
    } catch (error) {
      console.error("Error generating DRL from template:", error);
      alert(`Error generating DRL: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generateDRLFromTemplate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "DRL from Template"}
    </Button>
  );
};

export default GenerateDRLFromTemplate;
